import { CommonModule, Location } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, signal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NewAiSolutionUsagePhase2Request } from '@/app/interfaces/new-ai-solution-usage-phase2';
import { NewAiSolutionUsageService } from '@/app/services/ia-services/new-ai-solution-usage.service';
import { SearchService } from '@/app/services/ia-services/search.service';
import { ButtonsComponent } from '@/app/shared/buttons/buttons.component';
import { DatePickerComponent } from '@/app/shared/date-picker/date-picker.component';
import { PillComponent } from '@/app/shared/pill/pill.component';
import { getError } from '@/app/utils/form-errors';
import { integerOnlyValidator } from '@/app/utils/integer-validation';
import { DesyAngularModule } from 'desy-angular';
import { ConfirmationService } from 'primeng/api';
import { AutoComplete, AutoCompleteModule } from 'primeng/autocomplete';
import { ConfirmDialog } from 'primeng/confirmdialog';

@Component({
  selector: 'app-new-ai-solution-usage-phase2',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    AutoCompleteModule,
    ButtonsComponent,
    DatePickerComponent,
    PillComponent,
    DesyAngularModule,
    ConfirmDialog,
  ],
  templateUrl: './new-ai-solution-usage-phase2.component.html',
  styleUrl: './new-ai-solution-usage-phase2.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService],
})
export class NewAiSolutionUsagePhase2Component {
  readonly getError = getError;
  readonly formSubmitted = signal(false);
  private solutionId: number | null = null;
  readonly relatedProcesses = signal<{ code: number; name: string }[]>([]);
  proceduresList: { code: number; name: string }[] = [];
  procedureSearchLoading = false;
  procedureSearch: string | null = null;
  readonly relatedApplicationsOrSystems = signal<string[]>([]);
  readonly relatedApplicationOrSystem = signal('');
  readonly personalDataCategoryOptions = [
    { label: 'No aplica', value: 'no_aplica' },
    { label: 'Datos identificativos', value: 'datos_identificativos' },
    { label: 'Categorías especiales (salud, biométricos, etc.)', value: 'categorias_especiales' },
  ];
  readonly aiActClassificationOptions = [
    { label: 'Riesgo mínimo', value: 'minimo' },
    { label: 'Riesgo limitado', value: 'limitado' },
    { label: 'Riesgo alto', value: 'alto' },
    { label: 'Riesgo inaceptable', value: 'inaceptable' },
  ];

  readonly aiSolutionSubtypeOptions = [
    // { label: 'Herramienta corporativa', value: 'herramienta_corporativa' },
    // { label: 'Herramienta externa', value: 'herramienta_externa' },
    { label: 'Tipo A - Nativo M365', value: 'a_nativo' },
    { label: 'Tipo B - Agent Builder/Copilot Studio', value: 'b_agent' },
    { label: 'Tipo C - Externo', value: 'c_externo' },
  ];
  readonly scopeOfUseOptions = [
    { label: 'Personal', value: 'personal' },
    { label: 'Compartido o departamental', value: 'compartido_departamental' },
    { label: 'Publicación corporativa', value: 'publicacion_corporativa' },
  ];
  readonly autonomyLevelOptions = [
    { label: 'Solo responde a consultas', value: 'responde_consultas' },
    { label: 'Ejecuta acciones con confirmación', value: 'ejecuta_confirmacion' },
    { label: 'Ejecuta acciones sin confirmación', value: 'ejecuta_sin_confirmacion' },
    { label: 'Ejecución programada o reactiva', value: 'ejecucion_programada' },
  ];
  readonly personalDataProcessingOptions = [
    { label: 'No trata datos personales', value: 'no' },
    { label: 'Sí trata datos personales', value: 'si' },
    { label: 'Datos anonimizados previamente', value: 'anonimizados' },
  ];
  // readonly solutionStatusOptions = [
  //   { label: 'Activo', value: 'activo' },
  //   { label: 'En autorización', value: 'enAutorizacion' },
  //   { label: 'En revisión', value: 'enRevision' },
  //   { label: 'Suspendido', value: 'suspendido' },
  //   { label: 'Revocado', value: 'revocado' },
  // ];
  readonly enabledActionsOptions = [
    { label: 'Lectura y consulta', value: 'lectura_consulta' },
    { label: 'Creación de contenido', value: 'creacion_contenido' },
    { label: 'Envío de comunicaciones', value: 'envio_comunicaciones' },
    { label: 'Modificación de registros', value: 'modificacion_registros' },
    { label: 'Integración con terceros', value: 'integracion_terceros' },
  ];

  readonly form = this.formBuilder.group({
    solutionUsageName: [{ value: '', disabled: true }, Validators.required],
    solutionName: [{ value: '', disabled: true }, Validators.required],
    aiSolutionSubtype: ['', Validators.required],
    platformOrProvider: ['', Validators.required],
    version: ['', [Validators.required, Validators.min(1), integerOnlyValidator()]],
    registrationDate: this.formBuilder.control<Date | null>(null, Validators.required),
    department: [{ value: '', disabled: true }, Validators.required],
    requestingPerson: [{ value: '', disabled: true }, Validators.required],
    requestingPersonEmail: [{ value: '', disabled: true }, [Validators.required, Validators.email]],
    scopeOfUse: ['', Validators.required],
    expectedUse: ['', [Validators.required, Validators.maxLength(500)]],
    relatedProcess: this.formBuilder.control<string[]>([], Validators.required),
    relatedApplicationsOrSystems: this.formBuilder.control<string[]>([], Validators.required),
    enabledActions: ['', Validators.required],
    dataSources: ['', Validators.required],
    autonomyLevel: ['', Validators.required],
    personalDataProcessing: this.formBuilder.control<string | null>(null, Validators.required),
    personalDataCategory: ['', Validators.required],
    aiActClassification: ['', Validators.required],
    dataProtectionReference: ['', Validators.required],
    // riskLevel: ['', Validators.required],
    // traceabilityAndCompensatoryControls: ['', Validators.required],
  });

  constructor(
    private formBuilder: FormBuilder,
    private location: Location,
    private newAiSolutionUsageService: NewAiSolutionUsageService,
    private searchService: SearchService,
    private router: Router,
    private confirmationService: ConfirmationService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    const state = this.location.getState() as Record<string, unknown>;
    const phase2Prefill = state['phase2Prefill'];

    if (
      typeof phase2Prefill === 'object' &&
      phase2Prefill !== null &&
      'solutionId' in phase2Prefill &&
      typeof phase2Prefill.solutionId === 'number' &&
      Number.isInteger(phase2Prefill.solutionId) &&
      phase2Prefill.solutionId > 0 &&
      'solutionUsageName' in phase2Prefill &&
      typeof phase2Prefill.solutionUsageName === 'string' &&
      'solutionName' in phase2Prefill &&
      typeof phase2Prefill.solutionName === 'string' &&
      'department' in phase2Prefill &&
      typeof phase2Prefill.department === 'string' &&
      'requestingPerson' in phase2Prefill &&
      typeof phase2Prefill.requestingPerson === 'string' &&
      'requestingPersonEmail' in phase2Prefill &&
      typeof phase2Prefill.requestingPersonEmail === 'string'
    ) {
      this.solutionId = phase2Prefill.solutionId;
      this.form.patchValue({
        solutionUsageName: phase2Prefill.solutionUsageName,
        solutionName: phase2Prefill.solutionName,
        department: phase2Prefill.department,
        requestingPerson: phase2Prefill.requestingPerson,
        requestingPersonEmail: phase2Prefill.requestingPersonEmail,
      });
    }
  }

  confirm(): void {
    this.formSubmitted.set(true);
    this.form.markAllAsTouched();

    if (this.form.invalid || this.solutionId === null) {
      return;
    }

    this.confirmationService.confirm({
      message: '¿Seguro que desea completar la información del uso de solución IA?',
      header: 'Confirmación',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancelar',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Guardar',
      },
      accept: () => {
        void this.onSave();
      },
    });
  }

  async onSave(): Promise<void> {
    this.formSubmitted.set(true);
    this.form.markAllAsTouched();
    const solutionId = this.solutionId;

    if (this.form.invalid || solutionId === null) {
      return;
    }

    try {
      await this.newAiSolutionUsageService.createNewAiSolutionUsagePhase2(this.buildPayload(solutionId));
      await this.router.navigate(['/catalogo-ia/uso-de-solucion-ia/listado-uso-de-solucion-ia']);
    } catch {
      return;
    }
  }

  async onSearchProcedure(event: { query: string }): Promise<void> {
    this.procedureSearchLoading = true;
    this.proceduresList = [];
    try {
      const response = await this.searchService.search<{ data?: { code: number; name: string }[] }>({
        search: event.query,
        busqueda: 'procedimientos',
      });
      this.proceduresList = response.data || [];
    } finally {
      this.procedureSearchLoading = false;
      this.changeDetectorRef.markForCheck();
    }
  }

  searchCurrentProcedure(autocomplete: AutoComplete, event?: Event): void {
    const query = (this.procedureSearch ?? '').trim();
    if (query.length < 3) return;

    autocomplete.search(event, query, 'input');
  }

  onProcedureSearchEnter(event: Event, autocomplete: AutoComplete): void {
    const activeOption = (event.target as HTMLElement | null)?.getAttribute('aria-activedescendant');
    if (activeOption) return;

    event.preventDefault();
    this.searchCurrentProcedure(autocomplete, event);
  }

  onProcedureSelected(event: { value: { code: number; name: string } }): void {
    this.relatedProcesses.update((processes) => [...processes, event.value]);
    this.syncRelatedProcessControl();
    setTimeout(() => this.clearProcedureSearch());
  }

  clearProcedureSearch(): void {
    this.procedureSearch = null;
    this.proceduresList = [];
    this.procedureSearchLoading = false;
  }

  removeRelatedProcess(code: number): void {
    this.relatedProcesses.update((processes) => processes.filter((process) => process.code !== code));
    this.syncRelatedProcessControl();
  }

  addRelatedApplicationOrSystem(event?: MouseEvent): void {
    event?.preventDefault();
    this.form.controls.relatedApplicationsOrSystems.markAsTouched();
    const value = this.relatedApplicationOrSystem();
    if (value) {
      this.relatedApplicationsOrSystems.update((applications) => [...applications, value]);
      this.form.controls.relatedApplicationsOrSystems.setValue(this.relatedApplicationsOrSystems());
      this.relatedApplicationOrSystem.set('');
    }
  }

  removeRelatedApplicationOrSystem(item: string): void {
    this.relatedApplicationsOrSystems.update((applications) =>
      applications.filter((application) => application !== item)
    );
    this.form.controls.relatedApplicationsOrSystems.setValue(this.relatedApplicationsOrSystems());
  }

  backPage(): void {
    this.location.back();
  }

  preventInvalidVersionInput(event: InputEvent | ClipboardEvent): void {
    const inputValue =
      'clipboardData' in event ? (event.clipboardData?.getData('text') ?? '') : (event.data ?? '');

    if (inputValue && !/^\d+$/.test(inputValue)) {
      event.preventDefault();
    }
  }

  private syncRelatedProcessControl(): void {
    this.form.controls.relatedProcess.setValue(
      this.relatedProcesses().map((process) => `${process.code} - ${process.name}`)
    );
  }

  private buildPayload(solutionId: number): NewAiSolutionUsagePhase2Request {
    const rawFormData = this.form.getRawValue();

    return {
      solutionId,
      solutionUsageName: this.trim(rawFormData.solutionUsageName),
      solutionName: this.trim(rawFormData.solutionName),
      aiSolutionSubtype: this.trim(rawFormData.aiSolutionSubtype),
      platformOrProvider: this.trim(rawFormData.platformOrProvider),
      version: Number(rawFormData.version),
      registrationDate: this.formatDate(rawFormData.registrationDate),
      department: this.trim(rawFormData.department),
      requestingPerson: this.trim(rawFormData.requestingPerson),
      requestingPersonEmail: this.trim(rawFormData.requestingPersonEmail),
      scopeOfUse: this.trim(rawFormData.scopeOfUse),
      expectedUse: this.trim(rawFormData.expectedUse),
      relatedProcess: this.relatedProcesses()[0]?.code.toString() ?? '',
      relatedApplicationsOrSystems:
        (rawFormData.relatedApplicationsOrSystems ?? [])
          .map((applicationOrSystem) => this.trim(applicationOrSystem))
          .find((applicationOrSystem) => applicationOrSystem !== '') ?? '',
      enabledActions: [this.trim(rawFormData.enabledActions)].filter((action) => action !== ''),
      dataSources: this.trim(rawFormData.dataSources),
      autonomyLevel: this.trim(rawFormData.autonomyLevel),
      personalDataProcessing: this.trim(rawFormData.personalDataProcessing),
      personalDataCategory: this.trim(rawFormData.personalDataCategory),
      aiActClassification: this.trim(rawFormData.aiActClassification),
      dataProtectionReference: this.trim(rawFormData.dataProtectionReference),
    };
  }

  private trim(value: string | null | undefined): string {
    return (value ?? '').trim();
  }

  private formatDate(value: Date | null): string | null {
    return value ? value.toISOString() : null;
  }
}
